import ClickAwayListener from '@mui/material/ClickAwayListener';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import { useMemo, useState } from 'react';
import { Box, IconButton, Typography, alpha } from '@mui/material';
import { InfoOutlined as InfoIcon, HelpOutlineOutlined as HelpIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

type Props = {
  text: TooltipProps['title'];
  children?: JSX.Element;
  icon?: { size?: number; color?: string; help?: boolean };
  onHover?: boolean;
  onHoverCursor?: string;
  iconAriaLabel?: string;
} & Partial<TooltipProps>;

const TooltipInfo = (props: Props) => {
  const { t } = useTranslation();
  const { text, children, onHover, onHoverCursor, icon, sx, ...tooltipProps } = props;
  const [open, setOpen] = useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setOpen(true);
  };

  const title = useMemo(
    () => (
      <Typography
        variant='body2'
        color={theme => `${theme.palette.primary.contrastText} !important`}
      >
        {text}
      </Typography>
    ),
    [text],
  );

  return (
    <>
      {onHover ? (
        <>
          <Tooltip
            title={title}
            enterDelay={100}
            PopperProps={{
              sx,
            }}
            {...tooltipProps}
          >
            <Box sx={{ cursor: onHoverCursor }}>
              {children ? (
                children
              ) : icon?.help ? (
                <IconButton aria-label={props.iconAriaLabel || t('ariaHelpTooltip')}>
                  <HelpIcon
                    sx={theme => ({
                      color: icon?.color ?? theme.palette.secondary.main,
                      fontSize: icon?.size ?? 17,
                    })}
                  />
                </IconButton>
              ) : (
                <IconButton aria-label={props.iconAriaLabel || t('ariaInfoTooltip')}>
                  <InfoIcon
                    sx={theme => ({
                      fontSize: icon?.size ?? 17,
                      color: icon?.color ?? theme.palette.secondary.main,
                    })}
                  />
                </IconButton>
              )}
            </Box>
          </Tooltip>
        </>
      ) : (
        <ClickAwayListener onClickAway={handleTooltipClose}>
          <Tooltip
            PopperProps={{
              sx,
            }}
            onClose={handleTooltipClose}
            open={open}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={title}
            {...tooltipProps}
          >
            {children ? (
              children
            ) : (
              <IconButton
                onClick={handleTooltipOpen}
                onBlur={handleTooltipClose}
                aria-label={
                  props.iconAriaLabel || (icon?.help ? t('ariaHelpTooltip') : t('ariaInfoTooltip'))
                }
              >
                {icon?.help ? (
                  <HelpIcon
                    sx={theme => ({
                      color: icon?.color ?? alpha(theme.palette.secondary.main, 0.5),
                      fontSize: icon?.size ?? 17,
                    })}
                  />
                ) : (
                  <InfoIcon
                    sx={theme => ({
                      fontSize: icon?.size ?? 17,
                      color: icon?.color ?? alpha(theme.palette.secondary.main, 0.5),
                    })}
                  />
                )}
              </IconButton>
            )}
          </Tooltip>
        </ClickAwayListener>
      )}
    </>
  );
};

export default TooltipInfo;
